// src/pages/AttendanceOverview/components/HolidayListTab.js
import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Tabs,
  Row,
  Col,
  App,
  Space,
  Tooltip,
  Popconfirm,
  message,
  Grid,
  Drawer,
  Flex
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchHolidayList,
  addHoliday,
  addRestrictedHoliday,
  fetchCalendar,
  fetchAllEmployees,
  addLeaveToEmp,
  deleteHoliday,
  editHoliday,
} from "../attendanceOverviewThunk";
import {
  selectHolidayList,
  selectEmployees,
  selectAttendanceCalendar,
} from "../attendanceOverviewSelectors";
import { selectAuthInfo } from "../../Auth/authSelectors";
import dayjs from "dayjs";
import AttendanceCalendar from "./AttendanceCalendar";
import RestrictedHolidayModal from "./modals/RestrictedHolidayModal";
import HolidayModal from "./modals/HolidayModal";
import LeaveToEmployeeModal from "./modals/LeaveToEmployeeModal";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import RecordDateFilter from "../../../components/filters/RecordDateFilter";
import { EditOutlined, DeleteOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { formatTitleCaseLabel } from "../../../lib/functions";
import { accessChecker } from "../../../lib/functions";
import CommonDateRangeFilter from "../../../components/common/filters/CommonDateRangeFilter";

const { TabPane } = Tabs;

const HolidayListTab = ({ calendarData, loading }) => {
  const dispatch = useDispatch();
  const holidayList = useSelector(selectHolidayList) || [];
  const employees = useSelector(selectEmployees);
  const calendar = useSelector(selectAttendanceCalendar);
  const authInfo = useSelector(selectAuthInfo);
  const { modal } = App.useApp();
  const [filters, setFilters] = useState({
    fromDate: null,
    toDate: null,
    holiday_type: null,
  });

  const [disabled, setDisabled] = useState(false);
  const [holidayModal, setHolidayModal] = useState(false);
  const [leaveToEmpModal, setLeaveToEmpModal] = useState(false);
  const [restrictedHolidayModal, setRestrictedHolidayModal] = useState(false);

  const [editingHoliday, setEditingHoliday] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [pagination, setPagination] = useState({  current: 1,  pageSize: 10 });

  const {useBreakpoint} = Grid;
  const screens = useBreakpoint();

  useEffect(() => {
    dispatch(fetchHolidayList(filters));
  }, [dispatch, filters]);

  const columns = [
    { title: "No.", render: (_, __, i) => i + 1,},
    { title: "Holiday Name", dataIndex: "holiday_name", key: "holiday_name" },
    {
      title: "Holiday Date",
      dataIndex: "holiday_date",
      key: "holiday_date",
      render: (v) => (v ? dayjs(v).format("YYYY-MMM-DD") : "-"),
    },
    {
      title: "Holiday Type",
      dataIndex: "holiday_type",
      key: "holiday_type",
      render: (value) => {
        return formatTitleCaseLabel(value);
      },
    },
    { title: "Notes", dataIndex: "notes", key: "notes" },

    ...(accessChecker("approve_attendance")
      ? [
          {
            title: "Actions",
            key: "actions",
            align: "right",
            render: (_, record) => (
              <Space>
                <Tooltip title="Edit">
                  <Button
                    type="text"
                    icon={<EditOutlined style={{ color: "#1677ff" }} />}
                    onClick={() => handleEditHoliday(record)}
                  />
                </Tooltip>

                <Popconfirm
                  title="Are you sure to delete this holiday?"
                  okText="Yes"
                  cancelText="No"
                  onConfirm={() => handleDeleteHoliday(record.holiday_id)}
                >
                  <Tooltip title="Delete">
                    <Button
                      type="text"
                      icon={<DeleteOutlined style={{ color: "red" }} />}
                    />
                  </Tooltip>
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
  ];

  const holidayTypes = [
    {
      label: "National Holiday",
      value: "national_holiday",
    },
    {
      label: "Regional Holiday",
      value: "regional_holiday",
    },
  ];

  const handleEditHoliday = (record) => {
    setHolidayModal(true);
    setEditingHoliday(record);
  };

  const closeHolidayModal = () => {
    setHolidayModal(false);
    setEditingHoliday(null);
  };

  const handleDeleteHoliday = async (holidayId) => {
    try {
      await dispatch(deleteHoliday({ holiday_id: holidayId })).unwrap();
      message.success("Holiday deleted successfully!");
      dispatch(fetchHolidayList(filters));
    } catch (err) {
      message.error(err.message || "Failed to delete holiday");
    }
  };

  const openLeaveToEmoModal = async () => {
    setLeaveToEmpModal(true);
    await dispatch(fetchAllEmployees()).unwrap();
  };

  const handleHolidaySubmit = async ({
    holiday_type,
    holiday_name,
    holiday_date,
    notes,
    id, // optional — for edit mode
  }) => {
    try {
      setDisabled(true);

      // ✅ Validation
      if (!holiday_type)
        return modal.warning({ title: "Select Holiday Type", centered: true });
      if (!holiday_name?.trim())
        return modal.warning({
          title: "Holiday Name is required",
          centered: true,
        });
      if (!holiday_date)
        return modal.warning({ title: "Select Holiday Date", centered: true });
      if (!notes?.trim())
        return modal.warning({ title: "Notes is required", centered: true });

      // ✅ Prepare payload
      const payload = {
        holiday_type,
        holiday_name,
        holiday_date: dayjs(holiday_date).format("YYYY-MM-DD"),
        notes,
        status: "active",
        id: id,
      };

      // ✅ Dispatch API call (edit or add)
      if (id) {
        await dispatch(editHoliday(payload)).unwrap();
        modal.success({ title: "Holiday Updated Successfully" });
      } else {
        await dispatch(addHoliday(payload)).unwrap();
        modal.success({ title: "Holiday Added Successfully" });
      }

      // ✅ Refresh data
      await Promise.all([
        dispatch(fetchCalendar()).unwrap(),
        dispatch(fetchHolidayList(filters)).unwrap(),
      ]);
      setEditingHoliday(null);
      setHolidayModal(false);
    } catch (err) {
      modal.error({
        title: "Failed to Save Holiday",
        content: err.message || "An unexpected error occurred",
      });
    } finally {
      setDisabled(false);
    }
  };

  const handleLeaveToEmpSubmit = async ({
    leaveType,
    employee,
    from_date,
    to_date,
    notes,
  }) => {
    try {
      setDisabled(true);

      const payload = {
        attendance: leaveType.value,
        user_id: employee.id,
        emp_id: employee.id,
        from_date,
        to_date,
        notes,
        status: "approved",
      };

      await dispatch(addLeaveToEmp(payload)).unwrap();
      modal.success({ title: "Leave Applied to Employee Successfully" });

      await dispatch(fetchCalendar()).unwrap();
      setLeaveToEmpModal(false);
    } catch (err) {
      modal.error({
        title: "Failed to Apply Leave to Employee",
        content: err.message || "An unexpected error occurred",
      });
    } finally {
      setDisabled(false);
    }
  };

  const handleAddRestrictedHoliday = async ({
    restricted_holiday_date,
    notes,
  }) => {
    try {
      setDisabled(true);
      if (!notes?.trim()) return modal.warning({ title: "Notes is required" });
      const payload = {
        restricted_holiday_date,
        notes,
      };
      await dispatch(addRestrictedHoliday(payload)).unwrap();
      modal.success({ title: "Restricted Holiday Added Successfully" });

      await dispatch(fetchCalendar()).unwrap();
      setRestrictedHolidayModal(false);
    } catch (err) {
      modal.error({
        title: "Failed to Add Restricted Holiday",
        content: err.message || "An unexpected error occurred",
      });
    } finally {
      setDisabled(false);
    }
  };
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div style={{ padding: screens.xs ? 5:24 }}>
      <Flex justify="end" style={{ marginBottom: 12 }}>
  <Tooltip title="Help">
    <Button
      type="text"
      icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
      onClick={() => setHelpOpen(true)}
    />
  </Tooltip>
</Flex>
      <Tabs defaultActiveKey="all" type="line">
        {accessChecker("approve_attendance") && (
          <TabPane tab="All" key="all">
            <Row gutter={24}>
              <Col xs={24} md={10} lg={8}>
                <AttendanceCalendar
                  calendarData={calendarData}
                  loading={loading}
                />
              </Col>
              <Col xs={24} md={14} lg={16}>
                <Row gutter={[16, 16]} style={{ marginTop: "12px", marginBottom: "32px" }}>
                  
                  <Col>
                    <Button
                      onClick={() => setHolidayModal(true)}
                      type="default"
                      className="traic-button"
                      data-testid="holiday"
                    >
                      🎉 Holiday
                    </Button>
                  </Col>
                  {/* 🗓️ holiday  */}
                  <Col>
                    <Button
                      onClick={openLeaveToEmoModal}
                      type="default"
                      className="traic-button"
                      data-testid="leave_applied_modal"
                    >
                      🌴 Leave
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      onClick={() => setRestrictedHolidayModal(true)}
                      type="default"
                      className="traic-button"
                      data-testid="restricted_holiday"
                    >
                      📅 Restricted Holiday
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </TabPane>
        )}
        <TabPane tab="Holiday List" key="list">
          <Card className="holiday-filters mb-4 shadow-md rounded-xl">
            <div style={{ marginBottom: 12 }}>
              <Row gutter={[16, 16]} align="middle" style={{ gap: "0.7rem" }}>
                   <Col xs={24} sm={12} md={8} xl={5}>
                  <label>All Types</label>
                  <SearchListAutoComplete
                    optionsList={holidayTypes.map((c) => ({
                      sale_id: c.value,
                      display_text: c.label,
                    }))}
                    onSelect={(obj) =>
                      handleFilterChange("holiday_type", obj?.sale_id)
                    }
                    placeholder="Types"
                    showAddNew={false}
                    allowClear
                  />
                </Col>

                {/* <RecordDateFilter
                  filters={filters}
                  handleFilterChange={handleFilterChange}
                /> */}

              <CommonDateRangeFilter
                filters={filters}
                onChange={(fromDate, toDate) => {
                  handleFilterChange("fromDate", fromDate);
                  handleFilterChange("toDate", toDate);
                }}
              />
              </Row>
            </div>

            <Table
              columns={columns}
              dataSource={holidayList}
              rowKey={(r) => r.id || r.holiday_id || r.holiday_date}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,

                showSizeChanger: true,
                showQuickJumper: true,

                pageSizeOptions: [ "10", "20", "50", "100"],

                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,

                onChange: (page, pageSize) => {
                  setPagination({
                    current: page,
                    pageSize,
                  });
                },

                onShowSizeChange: (current, size) => {
                  setPagination({
                    current: 1,
                    pageSize: size,
                  });
                },
              }}
              bordered
              size={"middle"}
              scroll={{ x: "max-content" }}
            />
            {/* <style>{`
            @media (min-width: 1200px) and (max-width: 1560px) {
  .holiday-filters .ant-row .ant-col {
    flex: 0 0 23.33% !important;
    max-width: 23.33% !important;
  }
}

    @media (min-width: 768px) and (max-width: 1024px) {
      .holiday-filters .ant-row .ant-col {
        flex: 0 0 50% !important;
        max-width: 50% !important;
      }
    }
  `}</style> */}
          </Card>
        </TabPane>
      </Tabs>
      <Drawer
        title="Holiday & Attendance Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={460}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: 18,
              fontWeight: 600,
              marginBottom: 12,
              marginLeft: 12
            }}
          >
            Holiday & Leave Management
          </h2>

          <p style={{ color: "#555", marginBottom: 16, marginLeft:17 }}>
            This section helps manage public holidays,
            restricted holidays, employee leave assignments,
            and attendance calendar workflows.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: 14,
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            <strong>Available Features</strong>

            <ol style={{ paddingLeft: 20, marginTop: 10 }}>
              <li>Add public holidays for employees</li>
              <li>Create restricted holidays</li>
              <li>Assign leave directly to employees</li>
              <li>Manage holiday calendar operations</li>
              <li>Edit existing holiday entries</li>
              <li>Delete unwanted holiday records</li>
              <li>Filter holidays using date range</li>
              <li>Filter holidays based on holiday types</li>
              <li>Review attendance-related holiday activities</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: 14,
              borderRadius: 8,
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: 18,
                marginTop: 10,
                color: "#666",
              }}
            >
              <li>Holiday entries affect attendance workflows.</li>
              <li>Restricted holidays can be managed separately.</li>
              <li>Leave assignments impact employee attendance records.</li>
              <li>Date filters help quickly locate holiday entries.</li>
              <li>Only authorized users can manage holiday operations.</li>
            </ul>
          </div>
        </div>
      </Drawer>
      <HolidayModal
        visible={holidayModal}
        onClose={closeHolidayModal}
        onHolidaySubmit={handleHolidaySubmit}
        holidayTypes={[
          { label: "Public Holiday", value: "public" },
          { label: "Restricted Holiday", value: "restricted" },
        ]}
        initialData={editingHoliday}
        isDisabled={disabled}
      />

      <LeaveToEmployeeModal
        visible={leaveToEmpModal}
        onClose={() => setLeaveToEmpModal(false)}
        onSubmit={handleLeaveToEmpSubmit}
        allUsers={employees}
        weekEndDays={calendar?.weekendDays[0]?.weekend_days}
        isDisabled={disabled}
        isIndia={authInfo?.country === "India"}
      />

      <RestrictedHolidayModal
        visible={restrictedHolidayModal}
        onClose={() => setRestrictedHolidayModal(false)}
        onAddRestrictedHoliday={handleAddRestrictedHoliday}
        isDisabled={disabled}
      />
    </div>
  );
};

export default HolidayListTab;
